import { animate, inView } from './wt-motion.js';

let currentExperience;

// Match only these store-owned presentation assets; catalog photos and app widgets remain intact.
const redundantProductArt = new Set([
  'subscribe-today-for-10-off-on-every-order-1-1638833651772.png',
  'pf-5625d695--subscribe-today-for-10-off-on-every-shipment.png',
  'add-a-subheading-8-1637524370001.png',
  'pf-7df4342c--1961961099thecheckoutprocessisguaranteedtobe100.png'
]);
function legacyProductAsset(source) {
  try {
    const url = new URL(source, 'https://whitetail.invalid');
    if (url.origin !== 'https://cdn.shopify.com' || !url.pathname.startsWith('/s/files/1/0575/5401/0309/')) return '';
    return url.pathname.slice(url.pathname.lastIndexOf('/') + 1).toLowerCase().replace(/_\d+x\d*(?=\.png$)/, '');
  } catch { return ''; }
}

function legacyProductImageAsset(image) {
  const asset = legacyProductAsset(image.getAttribute('src'));
  const candidates = (image.getAttribute('srcset') || '').split(',').map(value => value.trim().split(/\s+/)[0]).filter(Boolean);
  return asset && candidates.every(value => legacyProductAsset(value) === asset) ? asset : '';
}

function productPresentationTargets(content, purchase) {
  const art = [];
  const surfaces = new Set();
  for (const panel of purchase.querySelectorAll('[data-pf-type="Image"]')) {
    const image = panel.querySelector('img');
    if (panel.tagName === 'DIV' && image && panel.children.length === 1 && panel.children[0] === image
      && redundantProductArt.has(legacyProductImageAsset(image))) art.push(panel);
  }
  for (const image of content.querySelectorAll('img')) {
    if (legacyProductImageAsset(image) !== 'lets-walk-im-a-good-boy-12-1638402839837.png') continue;
    const row = image.closest('[data-pf-type="Row"]');
    if (!row || row.closest('form') || row.querySelector('form') || !row.closest('[data-pf-type="Section"]')) continue;
    surfaces.add(row);
    for (const column of row.querySelectorAll('[data-pf-type="Column"]')) surfaces.add(column);
  }
  return { art, surfaces: [...surfaces] };
}

function createGalleryDescriptionPlacement(panel, box, form, gallery, purchase) {
  let mounted;
  function structure() {
    const row = form.querySelector(':scope > [data-pf-type="Row"]');
    const galleryColumn = gallery?.closest('[data-pf-type="Column"]');
    const galleryCell = galleryColumn?.parentElement;
    const purchaseCell = purchase.parentElement;
    if (!box.isConnected || !row || !gallery || gallery.closest('form') !== form || purchase.closest('form') !== form
      || box.closest('form') || !box.parentElement || box.parentElement.closest('form')
      || galleryCell === purchaseCell || row.children.length !== 2
      || galleryCell?.parentElement !== row || purchaseCell?.parentElement !== row
      || !galleryCell.classList.contains('pf-c') || !purchaseCell.classList.contains('pf-c')) return null;
    return { row, galleryCell, purchaseCell };
  }
  function restore() {
    if (!mounted) return;
    const { stage, slot, parent, next, ownsStage, hadDescriptionClass } = mounted;
    if (panel.parentNode === slot) {
      if (stage.isConnected && !stage.closest('form')) stage.after(panel);
      else panel.remove();
    }
    slot.remove();
    if (ownsStage) {
      if (box.parentNode === stage && stage.isConnected && parent.isConnected) {
        parent.insertBefore(box, stage.parentNode === parent ? stage : next?.parentNode === parent ? next : null);
      }
      // Leave replacement native nodes or another app's content in the document, never discard them with our wrapper.
      while (stage.firstChild) {
        if (stage.parentNode) stage.before(stage.firstChild);
        else stage.firstChild.remove();
      }
      stage.remove();
    } else if (!hadDescriptionClass) stage.classList.remove('wt-description-stage');
    mounted = undefined;
  }
  return {
    refresh() {
      const candidate = structure();
      if (mounted && candidate && candidate.row === mounted.row && candidate.galleryCell === mounted.galleryCell
        && candidate.purchaseCell === mounted.purchaseCell && box.parentElement === mounted.stage
        && mounted.stage.isConnected && mounted.slot.parentElement === mounted.stage && !mounted.slot.closest('form')) {
        for (const name of ['wt-product-stage', 'wt-description-stage']) {
          if (!mounted.stage.classList.contains(name)) mounted.stage.classList.add(name);
        }
        if (!mounted.slot.classList.contains('wt-description-slot')) mounted.slot.classList.add('wt-description-slot');
        if (panel.parentNode !== mounted.slot) mounted.slot.append(panel);
        return true;
      }
      restore();
      const target = structure();
      if (!target) return false;
      const existingStage = box.parentElement.classList.contains('wt-product-stage') ? box.parentElement : null;
      if (existingStage?.querySelector('.wt-bundle-slot')) return false;
      const stage = existingStage || document.createElement('div');
      const parent = existingStage ? stage.parentElement : box.parentElement;
      if (!parent || parent.closest('form')) return false;
      const next = existingStage ? stage.nextSibling : box.nextSibling;
      const hadDescriptionClass = stage.classList.contains('wt-description-stage');
      if (!existingStage) {
        stage.className = 'wt-product-stage wt-description-stage';
        stage.setAttribute('data-wt-product-id', String(box.getAttribute('data-product-id')));
        parent.insertBefore(stage, box);
        stage.append(box);
      } else if (!hadDescriptionClass) stage.classList.add('wt-description-stage');
      const slot = document.createElement('div');
      slot.id = 'WtDescriptionSlot';
      slot.className = 'wt-description-slot';
      stage.append(slot); slot.append(panel);
      mounted = { ...target, stage, slot, parent, next, ownsStage: !existingStage, hadDescriptionClass };
      return true;
    },
    restore
  };
}

function enhanceProduct() {
  const dataElement = document.getElementById('WtProductExperienceData');
  if (!dataElement) return;

  let data;
  try {
    data = JSON.parse(dataElement.textContent);
  } catch (error) {
    console.error('WhiteTail product settings could not be read.', error);
    return;
  }

  if (!Number.isInteger(data.productId) || !Array.isArray(data.variants) || !Array.isArray(data.optionNames) || !data.strings
    || data.variants.some(variant => !Number.isInteger(variant.id) || typeof variant.available !== 'boolean'
      || typeof variant.price !== 'string' || !Array.isArray(variant.options) || !Array.isArray(variant.sellingPlanAllocations))) {
    console.error('WhiteTail product settings are incomplete.');
    return;
  }

  const box = document.querySelector(`[data-pf-type="ProductBox"][data-product-id="${data.productId}"]`);
  if (currentExperience?.box === box && currentExperience.dataText === dataElement.textContent
    && box?.isConnected && currentExperience.form.isConnected && currentExperience.title.isConnected
    && currentExperience.addButton.isConnected && currentExperience.addButton.closest('form') === currentExperience.form
    && currentExperience.gallery === box.querySelector('[data-pf-type="ProductImage"]')
    && currentExperience.dock === document.getElementById('WtProductDock') && currentExperience.dock.isConnected) {
    currentExperience.refresh();
    return;
  }
  currentExperience?.destroy();
  currentExperience = undefined;
  if (!box) return;

  const form = box.querySelector('form');
  const title = box.querySelector('[data-pf-type="ProductTitle"]');
  const gallery = box.querySelector('[data-pf-type="ProductImage"]');
  const purchase = title?.closest('[data-pf-type="Column"]');
  const addButton = box.querySelector('[data-pf-type="ProductATC"]');
  const select = form?.querySelector('select[name="id"]');
  const dock = document.getElementById('WtProductDock');
  const selection = dock?.querySelector('[data-wt-selection]');
  const price = dock?.querySelector('[data-wt-price]');
  const availability = dock?.querySelector('[data-wt-availability]');
  const purchaseType = dock?.querySelector('[data-wt-purchase-type]');
  const returnButton = dock?.querySelector('[data-wt-return]');
  if (!form || !title || !purchase || !addButton || addButton.closest('form') !== form || !dock || !selection || !price || !availability || !purchaseType || !returnButton) {
    console.warn('WhiteTail product enhancements require the PageFly product controls.');
    return;
  }

  const events = new AbortController();
  const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const animations = new Set();
  const cleanups = [];
  const originalAttributes = new WeakMap();
  const ownedClasses = new WeakMap();
  const originalMotionStyles = new WeakMap();
  const presentationMarkers = new Map();
  const variants = new Map(data.variants.map(variant => [String(variant.id), variant]));
  const originalReturnLabel = returnButton.textContent;
  const originalSummary = [selection, price, availability, purchaseType].map(element => ({ element, text: element.textContent, hidden: element.hidden }));
  let dockAnimation;
  let nativeActionAboveViewport = false;
  let relatedChoicesVisible = false;
  let destroyed = false;
  let reviewControlIndex = 0;
  let quantitySummary;
  let shoppingObserver;
  const shoppingVisibility = new Map();
  const descriptionDisplay = ['expanded', 'accordion', 'hidden'].includes(data.descriptionDisplay) ? data.descriptionDisplay : 'expanded';
  const descriptionPosition = ['gallery', 'after_product', 'purchase_column'].includes(data.descriptionPosition) ? data.descriptionPosition : 'gallery';
  const dockEnabled = data.dockEnabled !== false;
  let descriptionPanel;
  let descriptionGallery;

  function placeDescription() {
    if (!descriptionPanel || destroyed) return;
    if (descriptionPosition === 'purchase_column') {
      if (purchase.lastElementChild !== descriptionPanel) purchase.append(descriptionPanel);
      return;
    }
    if (descriptionPosition === 'gallery' && descriptionGallery?.refresh()) return;
    const stage = box.closest('.wt-product-stage');
    const primarySection = box.closest('[data-pf-type="Section"]');
    const related = document.getElementById('WtRelatedBundles');
    const fullWidthBundle = related?.querySelector('[data-wt-bundle]');
    // Follow the related controller's settled outer anchor, never compete for its sibling position.
    const followsProduct = primarySection && related?.isConnected && primarySection.nextElementSibling === related
      && related.parentElement === primarySection.parentElement;
    const activeFullWidthBundle = followsProduct && !related.hidden && !related.closest('[hidden]')
      && fullWidthBundle && !fullWidthBundle.hidden && !fullWidthBundle.closest('[hidden]');
    const anchor = stage || (activeFullWidthBundle ? related : box);
    if (!anchor.isConnected || !anchor.parentElement || anchor.closest('form')) return;
    if (anchor.nextElementSibling !== descriptionPanel) anchor.after(descriptionPanel);
  }

  function setAttribute(element, name, value) {
    let attributes = originalAttributes.get(element);
    if (!attributes) {
      attributes = new Map();
      originalAttributes.set(element, attributes);
    }
    if (!attributes.has(name)) {
      const original = element.getAttribute(name);
      attributes.set(name, original);
      cleanups.push(() => {
        if (original === null) element.removeAttribute(name);
        else element.setAttribute(name, original);
      });
    }
    if (element.getAttribute(name) !== String(value)) element.setAttribute(name, value);
  }

  function addClass(element, name) {
    if (!element || element.classList.contains(name)) return;
    let classes = ownedClasses.get(element);
    if (!classes) {
      classes = new Set();
      ownedClasses.set(element, classes);
    }
    if (!classes.has(name)) {
      classes.add(name);
      cleanups.push(() => element.classList.remove(name));
    }
    element.classList.add(name);
  }

  function move(element, keyframes, duration = 0.3) {
    if (destroyed || motionPreference.matches || !element?.animate) return;
    let styles = originalMotionStyles.get(element);
    if (!styles) {
      styles = new Map();
      originalMotionStyles.set(element, styles);
    }
    for (const property of Object.keys(keyframes)) {
      if (styles.has(property)) continue;
      const value = element.style.getPropertyValue(property);
      const priority = element.style.getPropertyPriority(property);
      styles.set(property, value);
      cleanups.push(() => {
        if (value) element.style.setProperty(property, value, priority);
        else element.style.removeProperty(property);
      });
    }
    const animation = animate(element, keyframes, { duration, ease: [0.22, 1, 0.36, 1] });
    animations.add(animation);
    animation.then(() => animations.delete(animation));
    return animation;
  }

  motionPreference.addEventListener('change', () => {
    if (!motionPreference.matches) return;
    for (const animation of animations) animation.complete();
    animations.clear();
  }, { signal: events.signal });

  function repairPresentation() {
    addClass(box, 'wt-product-experience');
    addClass(form.querySelector(':scope > [data-pf-type="Row"]'), 'wt-product-layout');
    addClass(gallery?.closest('[data-pf-type="Column"]'), 'wt-product-gallery');
    addClass(purchase, 'wt-product-purchase');
    if (descriptionDisplay !== 'hidden' && ((descriptionPanel?.isConnected && descriptionPanel.querySelector('.wt-product-guide'))
      || purchase.querySelector('.wt-product-guide'))) addClass(box, 'wt-product-has-details');
    else if (ownedClasses.get(box)?.has('wt-product-has-details') && box.classList.contains('wt-product-has-details')) box.classList.remove('wt-product-has-details');
    setAttribute(title, 'role', 'heading');
    setAttribute(title, 'aria-level', '1');
    setAttribute(title, 'tabindex', '-1');
    if (!title.id) setAttribute(title, 'id', `WtProductTitle-${data.productId}`);
    const content = document.getElementById('MainContent');
    const targets = productPresentationTargets(content || box, purchase);
    for (const [attribute, nodes, value] of [
      ['data-wt-product-art', targets.art, 'redundant'],
      ['data-wt-product-story-surface', targets.surfaces, 'natural-chews']
    ]) {
      const next = new Set(nodes);
      for (const node of presentationMarkers.get(attribute) || []) {
        if (next.has(node)) continue;
        const original = originalAttributes.get(node)?.get(attribute);
        if (original === null) node.removeAttribute(attribute);
        else if (original !== undefined) node.setAttribute(attribute, original);
      }
      for (const node of next) setAttribute(node, attribute, value);
      presentationMarkers.set(attribute, next);
    }
  }
  repairPresentation();

  const eyebrow = document.createElement('p');
  eyebrow.className = 'wt-product-eyebrow';
  eyebrow.textContent = data.strings.brand;
  title.before(eyebrow);
  cleanups.push(() => eyebrow.remove());

  if (select) {
    if (!select.id) setAttribute(select, 'id', `WtProductOption-${data.productId}`);
    const label = document.createElement('label');
    label.className = 'wt-product-option-label';
    label.htmlFor = select.id;
    label.textContent = data.hasOnlyDefaultVariant ? data.strings.chooseOption : data.optionLabel;
    select.before(label);
    cleanups.push(() => label.remove());
  }
  setAttribute(returnButton, 'aria-controls', select && !data.hasOnlyDefaultVariant ? select.id : title.id);

  const purchaseTemplate = document.getElementById('WtProductPurchaseInfo');
  if (purchaseTemplate) {
    const purchaseContent = purchaseTemplate.content.cloneNode(true);
    const purchaseNodes = [...purchaseContent.childNodes];
    addButton.after(purchaseContent);
    cleanups.push(() => purchaseNodes.forEach(node => node.remove()));
  }

  const linksTemplate = document.getElementById('WtProductLinks');
  if (linksTemplate) {
    const linksContent = linksTemplate.content.cloneNode(true);
    const linksNodes = [...linksContent.childNodes];
    purchase.append(linksContent);
    cleanups.push(() => linksNodes.forEach(node => node.remove()));
  }

  const detailsTemplate = document.getElementById('WtProductDetails');
  if (detailsTemplate && descriptionDisplay !== 'hidden') {
    const detailsContent = detailsTemplate.content.cloneNode(true);
    const details = detailsContent.querySelector('.wt-product-guide');
    if (details) {
      descriptionPanel = document.createElement('div');
      descriptionPanel.className = 'wt-product-description';
      descriptionPanel.setAttribute('data-wt-description-position', descriptionPosition);
      descriptionPanel.append(detailsContent);
      if (descriptionPosition === 'gallery') {
        descriptionGallery = createGalleryDescriptionPlacement(descriptionPanel, box, form, gallery, purchase);
        cleanups.push(() => descriptionGallery.restore());
      }
      placeDescription();
      cleanups.push(() => descriptionPanel.remove());
      addClass(box, 'wt-product-has-details');
      if (details.tagName === 'DETAILS') {
        details.addEventListener('toggle', () => {
          if (details.open) move(details.querySelector('.wt-product-guide__body'), { opacity: [0.7, 1] }, 0.2);
        }, { signal: events.signal });
      }
      if ('IntersectionObserver' in window && !motionPreference.matches) {
        cleanups.push(inView(details, element => {
          move(element, { opacity: [0.85, 1], transform: ['translateY(8px)', 'translateY(0px)'] });
        }, { amount: 0.25 }));
      }
    }
  }

  function updateSelection() {
    if (destroyed) return;
    // Read successful controls without firing formdata events or modifying app state.
    function controlValue(name) {
      const control = [...form.elements].find(element => element.name === name && !element.matches(':disabled')
        && (!['radio', 'checkbox'].includes(element.type) || element.checked));
      return String(control?.value || '');
    }
    const variant = variants.get(controlValue('id'));
    const planId = controlValue('selling_plan').trim();
    const allocation = variant?.sellingPlanAllocations.find(plan => String(plan.id) === planId);
    const optionText = variant && !data.hasOnlyDefaultVariant
      ? variant.options.map((option, index) => `${data.optionNames[index] || data.optionLabel}: ${option}`).join(' / ')
      : '';
    const priceText = variant && (allocation || (!planId && !data.requiresSellingPlan))
      ? allocation?.price || variant.price
      : '';
    let purchaseText = '';
    if (planId) purchaseText = allocation?.name ? `${data.strings.subscriptionSelected}: ${allocation.name}` : data.strings.subscriptionSelected;
    else if (data.hasSellingPlans && !data.requiresSellingPlan) purchaseText = data.strings.oneTimeSelected;

    // Avoid observer loops when PageFly updates the same form subtree.
    function writeText(element, text) {
      if (element.textContent !== text) element.textContent = text;
    }
    writeText(selection, quantitySummary ? [optionText, quantitySummary.quantityText].filter(Boolean).join(' / ') : optionText);
    writeText(price, quantitySummary ? quantitySummary.totalText : priceText);
    writeText(availability, !variant ? data.strings.selectionUnavailable : variant.available ? data.strings.available : data.strings.soldOut);
    writeText(purchaseType, purchaseText);
    price.hidden = !(quantitySummary ? quantitySummary.totalText : priceText);
    purchaseType.hidden = !purchaseText;
    setAttribute(availability, 'data-wt-available', variant ? String(variant.available) : 'unknown');
  }

  updateSelection();
  form.addEventListener('change', updateSelection, { signal: events.signal });
  form.addEventListener('input', updateSelection, { signal: events.signal });
  form.addEventListener('wt:quantity-summary', event => {
    const summary = event.detail;
    if (!summary || typeof summary.quantityText !== 'string' || typeof summary.totalText !== 'string') return;
    quantitySummary = summary;
    updateSelection();
  }, { signal: events.signal });
  const variantObserver = new MutationObserver(() => {
    if (!addButton.isConnected) { enhanceProduct(); return; }
    placeDescription();
    repairPresentation();
    updateSelection();
  });
  variantObserver.observe(form, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['class', 'role', 'aria-level', 'tabindex', 'selected', 'value', 'checked', 'disabled', 'aria-disabled'] });
  cleanups.push(() => variantObserver.disconnect());

  returnButton.addEventListener('click', () => {
    const currentSelect = form.querySelector('select[name="id"]');
    const planControl = [...form.querySelectorAll('[name="selling_plan"]')]
      .find(control => !control.disabled && control.type !== 'hidden' && control.getClientRects().length);
    const target = currentSelect && !currentSelect.disabled && !data.hasOnlyDefaultVariant ? currentSelect : planControl || title;
    if (!target.id) setAttribute(target, 'id', `WtProductReview-${data.productId}-${++reviewControlIndex}`);
    setAttribute(returnButton, 'aria-controls', target.id);
    target.focus({ preventScroll: true });
    target.scrollIntoView({ behavior: motionPreference.matches ? 'auto' : 'smooth', block: 'center' });
  }, { signal: events.signal });

  function updateDock() {
    if (destroyed) return;
    const dockShouldBeVisible = dockEnabled && nativeActionAboveViewport && !relatedChoicesVisible;
    if (dockShouldBeVisible === !dock.hidden) return;
    if (!dockShouldBeVisible && dock.contains(document.activeElement)) return;
    dockAnimation?.cancel();
    animations.delete(dockAnimation);
    dock.hidden = !dockShouldBeVisible;
    if (dockShouldBeVisible) {
      updateSelection();
      dockAnimation = move(dock, { opacity: [0, 1], transform: ['translateY(12px)', 'translateY(0px)'] }, 0.24);
    }
  }

  function syncShoppingRegions() {
    if (!shoppingObserver || destroyed) return;
    const regions = [document.getElementById('WtRelatedBundles'), document.getElementById('WtBundleSlot')]
      .filter(region => region?.isConnected);
    for (const region of shoppingVisibility.keys()) {
      if (regions.includes(region)) continue;
      shoppingObserver.unobserve(region);
      shoppingVisibility.delete(region);
    }
    for (const region of regions) {
      if (shoppingVisibility.has(region)) continue;
      shoppingVisibility.set(region, false);
      shoppingObserver.observe(region);
    }
    relatedChoicesVisible = [...shoppingVisibility.values()].some(Boolean);
    updateDock();
  }

  dock.addEventListener('focusout', () => queueMicrotask(updateDock), { signal: events.signal });
  if ('IntersectionObserver' in window && dockEnabled) {
    const purchaseObserver = new IntersectionObserver(entries => {
      const entry = entries[0];
      nativeActionAboveViewport = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
      updateDock();
    });
    purchaseObserver.observe(addButton);
    cleanups.push(() => purchaseObserver.disconnect());
    shoppingObserver = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (shoppingVisibility.has(entry.target)) shoppingVisibility.set(entry.target, entry.isIntersecting);
      }
      relatedChoicesVisible = [...shoppingVisibility.values()].some(Boolean);
      updateDock();
    });
    syncShoppingRegions();
    cleanups.push(() => { shoppingObserver.disconnect(); shoppingVisibility.clear(); });
  }

  if (gallery) {
    function labelThumbnails() {
      const thumbnails = gallery.querySelectorAll('[data-pf-type="ImageItem"]');
      for (let index = 0; index < thumbnails.length; index += 1) {
        const thumbnail = thumbnails[index];
        setAttribute(thumbnail, 'role', 'button');
        setAttribute(thumbnail, 'tabindex', '0');
        setAttribute(thumbnail, 'aria-label', `${data.strings.imageLabel} ${index + 1}`);
        setAttribute(thumbnail, 'aria-pressed', String(thumbnail.getAttribute('data-active') === 'true'));
      }
    }
    labelThumbnails();
    gallery.addEventListener('keydown', event => {
      const thumbnail = event.target.closest('[data-pf-type="ImageItem"]');
      if (thumbnail && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        thumbnail.click();
      }
    }, { signal: events.signal });
    const galleryObserver = new MutationObserver(labelThumbnails);
    galleryObserver.observe(gallery, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-active'] });
    cleanups.push(() => galleryObserver.disconnect());
    move(gallery, { opacity: [0.85, 1], transform: ['translateY(8px)', 'translateY(0px)'] }, 0.38);
  }

  move(title, { opacity: [0.85, 1], transform: ['translateY(6px)', 'translateY(0px)'] });
  currentExperience = {
    box,
    form,
    title,
    addButton,
    gallery,
    dock,
    dataText: dataElement.textContent,
    refresh() { placeDescription(); repairPresentation(); updateSelection(); syncShoppingRegions(); },
    destroy() {
      destroyed = true;
      events.abort();
      if (dock.contains(document.activeElement) && title.isConnected) title.focus({ preventScroll: true });
      for (const animation of animations) animation.cancel();
      for (const cleanup of cleanups) cleanup();
      dock.hidden = true;
      returnButton.textContent = originalReturnLabel;
      for (const original of originalSummary) {
        original.element.textContent = original.text;
        original.element.hidden = original.hidden;
      }
    }
  };
}

let contentObserver;
function observeContent() {
  const content = document.getElementById('MainContent');
  if (!content || contentObserver) return;
  contentObserver = new MutationObserver(records => {
    // Bundle visibility changes placement; our own dock summary visibility must not restart enhancement.
    if (records.some(record => record.attributeName !== 'hidden' || record.target.closest('.wt-related-bundles'))) enhanceProduct();
  });
  contentObserver.observe(content, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'role', 'aria-level', 'tabindex', 'hidden', 'src', 'srcset'] });
}
function disconnectContent() {
  contentObserver?.disconnect();
  contentObserver = undefined;
}
function initializeProduct() { observeContent(); enhanceProduct(); }
initializeProduct();
document.addEventListener('DOMContentLoaded', initializeProduct, { once: true });
document.addEventListener('shopify:section:load', initializeProduct);
document.addEventListener('shopify:section:unload', event => {
  if (!currentExperience || !event.target.contains(currentExperience.box)) return;
  disconnectContent();
  currentExperience.destroy();
  currentExperience = undefined;
});
window.addEventListener('pagehide', () => {
  disconnectContent();
  currentExperience?.destroy();
  currentExperience = undefined;
});
window.addEventListener('pageshow', initializeProduct);
